/**
 * 엑셀/CSV 파일 처리 유틸리티
 * - 파일 읽기 (엑셀, CSV)
 * - 엑셀 양식 다운로드
 */

import * as XLSX from 'xlsx';

/**
 * 엑셀/CSV 파일을 읽어서 JSON 배열로 변환
 * @param {File} file - 업로드된 파일
 * @returns {Promise<Array>} 파싱된 데이터 배열
 */
export const readExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          defval: '', // 빈 셀을 빈 문자열로 처리
          raw: false // 날짜를 문자열로 변환
        });
        resolve(jsonData);
      } catch (error) {
        reject(new Error(`파일 읽기 실패: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };
    
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file, 'UTF-8');
    } else {
      reader.readAsBinaryString(file);
    }
  });
};

/**
 * 엑셀 파일의 모든 시트를 읽어서 객체로 반환
 * @param {File} file - 업로드된 엑셀 파일
 * @returns {Promise<Object>} { sheetName: [데이터 배열] } 형태의 객체
 */
export const readExcelFileAllSheets = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const result = {};
        
        // 모든 시트 읽기
        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            defval: '', // 빈 셀을 빈 문자열로 처리
            raw: false // 날짜를 문자열로 변환
          });
          result[sheetName] = jsonData;
        });
        
        resolve(result);
      } catch (error) {
        reject(new Error(`파일 읽기 실패: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };
    
    if (file.name.endsWith('.csv')) {
      reject(new Error('CSV 파일은 단일 시트만 지원합니다. 엑셀 파일(.xlsx, .xls)을 사용하세요.'));
    } else {
      reader.readAsBinaryString(file);
    }
  });
};

/**
 * CSV 파일을 읽어서 JSON 배열로 변환
 * @param {File} file - 업로드된 CSV 파일
 * @returns {Promise<Array>} 파싱된 데이터 배열
 */
export const readCSVFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const workbook = XLSX.read(text, { type: 'string' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          defval: '',
          raw: false
        });
        resolve(jsonData);
      } catch (error) {
        reject(new Error(`CSV 파일 읽기 실패: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('CSV 파일 읽기 중 오류가 발생했습니다.'));
    };
    
    reader.readAsText(file, 'UTF-8');
  });
};

/**
 * 데이터를 엑셀 파일로 다운로드
 * @param {Array} data - 다운로드할 데이터 배열
 * @param {Array} columns - 컬럼 정의 배열 [{ key: 'field_name', label: '컬럼명' }]
 * @param {String} fileName - 파일명
 */
export const downloadExcelTemplate = (columns, fileName) => {
  // 헤더만 있는 빈 데이터 생성
  const headerRow = {};
  columns.forEach(col => {
    headerRow[col.key] = col.label || col.key;
  });
  
  const data = [headerRow];
  
  // 워크북 생성
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
  // 컬럼 너비 설정
  const colWidths = columns.map(col => ({ wch: col.width || 15 }));
  ws['!cols'] = colWidths;
  
  // 파일 다운로드
  XLSX.writeFile(wb, fileName);
};

/**
 * 데이터를 엑셀 파일로 다운로드 (데이터 포함)
 * @param {Array} data - 다운로드할 데이터 배열
 * @param {Array} columns - 컬럼 정의 배열
 * @param {String} fileName - 파일명
 */
export const downloadExcelFile = (data, columns, fileName) => {
  // 컬럼 매핑 적용
  const mappedData = data.map(row => {
    const mappedRow = {};
    columns.forEach(col => {
      mappedRow[col.label || col.key] = row[col.key] || '';
    });
    return mappedRow;
  });
  
  // 워크북 생성
  const ws = XLSX.utils.json_to_sheet(mappedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
  // 컬럼 너비 설정
  const colWidths = columns.map(col => ({ wch: col.width || 15 }));
  ws['!cols'] = colWidths;
  
  // 파일 다운로드
  XLSX.writeFile(wb, fileName);
};

/**
 * 각 테이블별 컬럼 정의
 */
export const TABLE_COLUMNS = {
  timeInput: [
    { key: 'timelog_id', label: 'TimeLog_ID', width: 15 },
    { key: 'employee_code', label: '담당자', width: 15 },
    { key: 'log_date', label: '업무일자', width: 15 },
    { key: 'company_code', label: '업체코드', width: 15 },
    { key: 'contract_id', label: '계약번호', width: 15 },
    { key: 'task_id', label: 'Task_ID', width: 15 },
    { key: 'input_description', label: '상세내용(입력)', width: 30 },
    { key: 'input_minute', label: '투입시간(분)', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  todo_list: [
    { key: 'todo_id', label: 'Todo_ID', width: 15 },
    { key: 'employee_code', label: '담당자', width: 15 },
    { key: 'company_code', label: '업체코드', width: 15 },
    { key: 'contract_id', label: '계약번호', width: 15 },
    { key: 'task_id', label: 'Task_ID', width: 15 },
    { key: 'status', label: '진행상태', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  customer: [
    { key: 'company_sk', label: 'Surrogate Key', width: 15 },
    { key: 'company_code', label: '업체코드', width: 15 },
    { key: 'company_name_kr', label: '업체명(국문)', width: 25 },
    { key: 'company_name_en', label: '업체명(영문)', width: 25 },
    { key: 'business_number', label: '사업자번호', width: 15 },
    { key: 'representative_name', label: '대표자_성명', width: 15 },
    { key: 'aeo_certified', label: 'AEO인증유무', width: 15 },
    { key: 'aeo_grade', label: 'AEO등급', width: 15 },
    { key: 'aeo_first_certified_at', label: '최초 공인일자', width: 15 },
    { key: 'aeo_last_renewed_at', label: '갱신 공인일자', width: 15 },
    { key: 'aeo_expire_at', label: '공인 만료일자', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'start_date', label: '유효 시작일', width: 15 },
    { key: 'end_date', label: '유효 종료일', width: 15 },
    { key: 'is_current', label: '현재 유효 여부', width: 15 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  contract: [
    { key: 'company_code', label: '업체코드', width: 15 },
    { key: 'contract_id', label: '계약번호', width: 15 },
    { key: 'has_contract_file', label: '계약서_유무', width: 15 },
    { key: 'contract_date', label: '계약일자', width: 15 },
    { key: 'contract_title', label: '계약서_명칭', width: 30 },
    { key: 'contract_details', label: '계약내용', width: 40 },
    { key: 'cont_start_date', label: '계약_개시일', width: 15 },
    { key: 'cont_end_date', label: '계약_종료일', width: 15 },
    { key: 'contract_pic1', label: '계약_담당자(1)', width: 15 },
    { key: 'contract_pic2', label: '계약_담당자(2)', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  employee: [
    { key: 'employee_code', label: '직원코드', width: 15 },
    { key: 'employee_name', label: '직원명', width: 15 },
    { key: 'dept_code', label: '소속_부서코드', width: 15 },
    { key: 'dept_name', label: '소속_부서명', width: 20 },
    { key: 'is_title', label: '직책_유무', width: 15 },
    { key: 'title_name', label: '직책명', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  temp_list: [
    { key: 'templog_id', label: 'TempLog_ID', width: 15 },
    { key: 'employee_code', label: '담당자', width: 15 },
    { key: 'status', label: '진행상태', width: 15 },
    { key: 'log_date', label: '업무일자', width: 15 },
    { key: 'company_code', label: '업체코드', width: 15 },
    { key: 'mapped_contract_id', label: '맵핑_계약번호', width: 15 },
    { key: 'mapped_task_id', label: '맵핑_Task_ID', width: 15 },
    { key: 'input_description', label: '상세내용(입력)', width: 30 },
    { key: 'input_minute', label: '투입시간(분)', width: 15 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
  work: [
    { key: 'contract_id', label: '계약번호', width: 15 },
    { key: 'task_id', label: 'Task_ID', width: 15 },
    { key: 'task_description', label: '업무_명칭', width: 30 },
    { key: 'job_start_date', label: '업무_개시일', width: 15 },
    { key: 'job_end_date', label: '업무_종료일', width: 15 },
    { key: 'job_status', label: '진행상태', width: 15 },
    { key: 'job_pic1', label: '관리_담당자(1)', width: 15 },
    { key: 'job_pic2', label: '관리_담당자(2)', width: 15 },
    { key: 'customer_pic_name', label: '고객사_성명', width: 15 },
    { key: 'customer_pic_email', label: '고객사_이메일', width: 25 },
    { key: 'note_1', label: '비고1', width: 20 },
    { key: 'note_2', label: '비고2', width: 20 },
    { key: 'note_3', label: '비고3', width: 20 },
    { key: 'note_4', label: '비고4', width: 20 },
    { key: 'note_5', label: '비고5', width: 20 },
    { key: 'created_at', label: '생성일자', width: 20 },
    { key: 'created_id', label: '생성자', width: 15 },
    { key: 'updated_at', label: '수정일자', width: 20 },
    { key: 'updated_id', label: '수정자', width: 15 },
  ],
};

